// src/components/FinReports.js — Open Invoices, Sales by Item, Expenses by Vendor, fiscal-year setting
// [marker: fin-reports v1]
import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { RangePicker, rangePreset, loadFiscalStart, setFiscalStartLocal } from './AccountReports';

const money = (n) => {
  const v = parseFloat(n || 0);
  return (v < 0 ? '-' : '') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const num = (n) => { const v = parseFloat(n || 0); return Number.isInteger(v) ? String(v) : v.toFixed(2); };
const fmtDay = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const KIND = { check: 'Check', bank_deposit: 'Deposit', charge: 'Card charge', cc_credit: 'Card credit', transfer: 'Transfer', journal: 'Journal' };
const REF = { invoice: 'Invoice', receipt: 'Payment', deposit: 'Deposit', expense: 'Expense', qb_import: 'QuickBooks' };

const Stat = ({ label, value, tone }) => (
  <div className="stat-card" style={{ flex: '1 1 160px' }}>
    <div className="stat-label">{label}</div>
    <div className="stat-value" style={{ color: tone }}>{value}</div>
  </div>
);
const Shell = ({ controls, children }) => (
  <div>
    <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
      {controls}
      <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨 Print</button>
    </div>
    {children}
  </div>
);
const th = { textAlign: 'right', padding: '6px 8px', fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gray-600)', whiteSpace: 'nowrap' };
const td = { textAlign: 'right', padding: '5px 8px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontSize: '0.875rem' };

// Rows that open to show what's inside them
function GroupTable({ groups, cols, renderLines, total, totalLabel = 'TOTAL', extraFooter }) {
  const [open, setOpen] = useState({});
  return (
    <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid var(--gray-200)', background: 'var(--gray-50)' }}>
            <th style={{ ...th, textAlign: 'left' }}></th>
            {cols.map(c => <th key={c.label} style={th}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {groups.length === 0 && <tr><td colSpan={cols.length + 1} style={{ padding: 20, color: 'var(--gray-500)', textAlign: 'center' }}>Nothing in this period.</td></tr>}
          {groups.map(g => (
            <React.Fragment key={g.key}>
              <tr className="rpt-row" style={{ borderTop: '1px solid var(--gray-100)', cursor: 'pointer' }} onClick={() => setOpen(o => ({ ...o, [g.key]: !o[g.key] }))}>
                <td style={{ padding: '6px 8px', fontWeight: 600, fontSize: '0.9rem' }}>
                  <span style={{ display: 'inline-block', width: 14, color: 'var(--gray-400)' }}>{open[g.key] ? '▾' : '▸'}</span>{g.label}
                  {g.note && <span style={{ fontWeight: 400, color: 'var(--gray-400)', fontSize: '0.75rem', marginLeft: 6 }}>{g.note}</span>}
                </td>
                {cols.map(c => <td key={c.label} style={{ ...td, color: c.tone ? c.tone(g) : undefined }}>{c.get(g)}</td>)}
              </tr>
              {open[g.key] && (
                <tr><td colSpan={cols.length + 1} style={{ padding: '2px 8px 10px 30px', background: '#fafbfc' }}>{renderLines(g)}</td></tr>
              )}
            </React.Fragment>
          ))}
          {total && (
            <tr style={{ borderTop: '2px solid var(--gray-300)', background: 'var(--gray-50)' }}>
              <td style={{ padding: '7px 8px', fontWeight: 700 }}>{totalLabel}</td>
              {cols.map(c => <td key={c.label} style={{ ...td, fontWeight: 700 }}>{c.total ? c.total(total) : ''}</td>)}
            </tr>
          )}
          {extraFooter}
        </tbody>
      </table>
    </div>
  );
}
const MiniTable = ({ head, rows }) => (
  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
    <thead><tr>{head.map((h, i) => <th key={i} style={{ ...th, textAlign: h.right ? 'right' : 'left', fontSize: '0.68rem' }}>{h.label}</th>)}</tr></thead>
    <tbody>{rows}</tbody>
  </table>
);
const sc = { padding: '3px 8px', borderTop: '1px solid var(--gray-100)' };
const scR = { ...sc, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

// ─── Fiscal year setting ──────────────────────────────────────────────────────
export function FiscalYearSetting({ canEdit, onChanged }) {
  const [m, setM] = useState(null);
  useEffect(() => { loadFiscalStart().then(setM); }, []);
  if (m === null) return null;
  const save = async (v) => {
    const prev = m; setM(v);
    try { await api.put('/fin-reports/fiscal', { startMonth: v }); setFiscalStartLocal(v); toast.success(`Fiscal year now starts in ${MONTHS[v - 1]}.`); onChanged && onChanged(); }
    catch (err) { setM(prev); toast.error(err.response?.data?.error || 'Could not save.'); }
  };
  return (
    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', color: 'var(--gray-600)' }} title="Used for the fiscal-year date choices on every report">
      Fiscal year starts in
      <select className="form-select" style={{ width: 'auto', height: 32 }} value={m} disabled={!canEdit} onChange={e => save(parseInt(e.target.value, 10))}>
        {MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
      </select>
    </label>
  );
}

// ─── Open invoices / outstanding balances ─────────────────────────────────────
const BUCKETS = [
  { key: 'cur', label: 'Current', test: d => d <= 0 },
  { key: 'b30', label: '1–30 days', test: d => d >= 1 && d <= 30 },
  { key: 'b60', label: '31–60', test: d => d >= 31 && d <= 60 },
  { key: 'b90', label: '61–90', test: d => d >= 61 && d <= 90 },
  { key: 'old', label: 'Over 90', test: d => d > 90 },
];
export function OpenInvoicesReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [view, setView] = useState('customer');
  const [type, setType] = useState('all');
  useEffect(() => {
    api.get('/fin-reports/open-invoices').then(r => setRows(r.data || [])).catch(() => toast.error('Could not load open invoices.'));
  }, []);
  const shown = useMemo(() => (rows || []).filter(r => type === 'all' || r.invoice_type === type), [rows, type]);
  const groups = useMemo(() => {
    const m = new Map();
    shown.forEach(r => {
      const g = m.get(r.customer_key) || { key: r.customer_key, label: r.customer, link: r.business_id ? `/members/${r.business_id}` : r.contact_id ? `/contacts/${r.contact_id}` : null, invoices: [], total: 0, oldest: 0 };
      BUCKETS.forEach(b => { g[b.key] = g[b.key] || 0; });
      g.invoices.push(r); g.total += parseFloat(r.balance_due);
      const d = parseInt(r.days_overdue, 10) || 0; g.oldest = Math.max(g.oldest, d);
      const b = BUCKETS.find(x => x.test(d)); g[b.key] += parseFloat(r.balance_due);
      m.set(r.customer_key, g);
    });
    return Array.from(m.values()).sort((a, b) => b.total - a.total);
  }, [shown]);
  const total = useMemo(() => {
    const t = { total: 0 }; BUCKETS.forEach(b => { t[b.key] = groups.reduce((s, g) => s + g[b.key], 0); });
    t.total = groups.reduce((s, g) => s + g.total, 0); return t;
  }, [groups]);
  if (!rows) return <div className="spinner" />;
  const overdue = shown.filter(r => parseInt(r.days_overdue, 10) > 0);
  const gridCols = [
    { key: 'invoice_number', label: 'Invoice #', width: 110 },
    { key: 'customer', label: 'Customer', width: 220 },
    { key: 'invoice_type', label: 'Type', width: 100 },
    { key: 'issue_date', label: 'Date', width: 105, type: 'date' },
    { key: 'due_date', label: 'Due', width: 105, type: 'date' },
    { key: 'days_overdue', label: 'Days Late', width: 85, type: 'number' },
    { key: 'total_amount', label: 'Total', width: 105, type: 'money' },
    { key: 'amount_paid', label: 'Paid', width: 100, type: 'money', defaultHidden: true },
    { key: 'balance_due', label: 'Open Balance', width: 115, type: 'money' },
    { key: 'items', label: 'For', width: 260 },
    { key: 'sent_on', label: 'Sent', width: 100, type: 'date', defaultHidden: true },
    { key: 'email', label: 'Email', width: 190, defaultHidden: true },
  ];
  return (
    <Shell controls={<>
      <select className="form-select" style={{ width: 'auto', height: 34 }} value={view} onChange={e => setView(e.target.value)}>
        <option value="customer">By customer (aging)</option>
        <option value="list">Every open invoice</option>
      </select>
      <select className="form-select" style={{ width: 'auto', height: 34 }} value={type} onChange={e => setType(e.target.value)}>
        <option value="all">All invoice types</option>
        <option value="membership">Membership dues</option>
        <option value="sponsorship">Sponsorships</option>
        <option value="general">General</option>
      </select>
    </>}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Stat label="Total Outstanding" value={money(total.total)} />
        <Stat label="Overdue" value={money(overdue.reduce((s, r) => s + parseFloat(r.balance_due), 0))} tone="var(--danger)" />
        <Stat label="Open Invoices" value={shown.length} />
        <Stat label="Customers" value={groups.length} />
      </div>
      {view === 'list' ? (
        <DataGrid gridKey="rpt-open-invoices" rows={shown} columns={gridCols} onRowClick={r => navigate(`/invoices/${r.id}`)} emptyText="No open invoices." />
      ) : (
        <GroupTable
          groups={groups}
          total={total}
          cols={[
            ...BUCKETS.map(b => ({ label: b.label, get: g => (g[b.key] ? money(g[b.key]) : ''), total: t => money(t[b.key]),
              tone: b.key === 'cur' ? undefined : (g => (g[b.key] ? 'var(--danger)' : undefined)) })),
            { label: 'Total Open', get: g => <strong>{money(g.total)}</strong>, total: t => money(t.total) },
          ]}
          renderLines={g => (
            <>
              {g.link && <div style={{ fontSize: '0.78rem', margin: '4px 0' }}><Link to={g.link}>Open {g.label} →</Link></div>}
              <MiniTable head={[{ label: 'Invoice' }, { label: 'Date' }, { label: 'Due' }, { label: 'For' }, { label: 'Days late', right: true }, { label: 'Total', right: true }, { label: 'Open', right: true }]}
                rows={g.invoices.map(r => (
                  <tr key={r.id}>
                    <td style={sc}><Link to={`/invoices/${r.id}`}>{r.invoice_number}</Link></td>
                    <td style={sc}>{fmtDay(r.issue_date)}</td>
                    <td style={sc}>{fmtDay(r.due_date)}</td>
                    <td style={{ ...sc, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.items || ''}>{r.items || '—'}</td>
                    <td style={{ ...scR, color: parseInt(r.days_overdue, 10) > 0 ? 'var(--danger)' : undefined }}>{parseInt(r.days_overdue, 10) || ''}</td>
                    <td style={scR}>{money(r.total_amount)}</td>
                    <td style={{ ...scR, fontWeight: 600 }}>{money(r.balance_due)}</td>
                  </tr>
                ))} />
            </>
          )}
        />
      )}
      <p className="form-hint no-print" style={{ marginTop: 10 }}>Sent, overdue and partly paid invoices with money still owed (drafts and void invoices are left out). Click a customer to see their invoices.</p>
    </Shell>
  );
}

// ─── Sales by item / type / category / event / customer / account ─────────────
const SALES_GROUPS = {
  item: { label: 'Item', get: l => l.item_name || l.line_name },
  item_type: { label: 'Item type', get: l => l.item_type },
  category: { label: 'Category', get: l => l.category || '(no category)' },
  event: { label: 'Event', get: l => l.event_name || '(not for an event)' },
  customer: { label: 'Customer', get: l => l.customer },
  account: { label: 'Income account', get: l => l.income_account || '(default income account)' },
  month: { label: 'Month', get: l => l.date.slice(0, 7), sortKey: true },
};
export function SalesReport() {
  const [range, setRange] = useState(rangePreset('ytd'));
  const [by, setBy] = useState('item_type');
  const [status, setStatus] = useState('all');
  const [data, setData] = useState(null);
  useEffect(() => { loadFiscalStart().then(m => { if (m !== 1) setRange(rangePreset('fy_to_date')); }); }, []);
  useEffect(() => {
    setData(null);
    api.get(`/fin-reports/sales?from=${range.from}&to=${range.to}&status=${status}`).then(r => setData(r.data))
      .catch(() => toast.error('Could not load sales.'));
  }, [range, status]);
  const groups = useMemo(() => {
    if (!data) return [];
    const g = SALES_GROUPS[by]; const m = new Map();
    data.lines.forEach(l => {
      const k = String(g.get(l));
      const x = m.get(k) || { key: k, label: g.sortKey ? new Date(`${k}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : k, lines: [], qty: 0, amount: 0, invoices: new Set() };
      x.lines.push(l); x.qty += parseFloat(l.quantity || 0); x.amount += parseFloat(l.line_total || 0); x.invoices.add(l.invoice_id);
      m.set(k, x);
    });
    const arr = Array.from(m.values());
    return g.sortKey ? arr.sort((a, b) => a.key.localeCompare(b.key)) : arr.sort((a, b) => b.amount - a.amount);
  }, [data, by]);
  const gross = groups.reduce((s, g) => s + g.amount, 0);
  const disc = data ? data.discounts : 0;
  return (
    <Shell controls={<>
      <RangePicker value={range} onChange={setRange} />
      <label style={{ fontSize: '0.85rem', display: 'inline-flex', gap: 6, alignItems: 'center' }}>Group by
        <select className="form-select" style={{ width: 'auto', height: 34 }} value={by} onChange={e => setBy(e.target.value)}>
          {Object.entries(SALES_GROUPS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </label>
      <select className="form-select" style={{ width: 'auto', height: 34 }} value={status} onChange={e => setStatus(e.target.value)}>
        <option value="all">Everything billed</option>
        <option value="paid">Paid invoices only</option>
        <option value="unpaid">Unpaid invoices only</option>
      </select>
    </>}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Sales by {SALES_GROUPS[by].label}</div>
        <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{fmtDay(range.from)} – {fmtDay(range.to)}</div>
      </div>
      {!data ? <div className="spinner" /> : (
        <GroupTable
          groups={groups}
          total={{ qty: groups.reduce((s, g) => s + g.qty, 0), amount: gross }}
          totalLabel={disc ? 'Total before discounts' : 'TOTAL'}
          cols={[
            { label: 'Invoices', get: g => g.invoices.size, total: () => new Set(data.lines.map(l => l.invoice_id)).size },
            { label: 'Qty', get: g => num(g.qty), total: t => num(t.qty) },
            { label: 'Amount', get: g => <strong>{money(g.amount)}</strong>, total: t => money(t.amount) },
            { label: '% of Sales', get: g => (gross ? `${((g.amount / gross) * 100).toFixed(1)}%` : ''), total: () => (gross ? '100%' : '') },
            { label: 'Avg Price', get: g => (g.qty ? money(g.amount / g.qty) : ''), total: () => '' },
          ]}
          extraFooter={disc ? (
            <>
              <tr><td style={{ padding: '4px 8px' }}>Less invoice discounts</td><td /><td /><td style={{ ...td, color: 'var(--danger)' }}>-{money(disc)}</td><td /><td /></tr>
              <tr style={{ borderTop: '2px solid var(--gray-300)', background: 'var(--gray-50)' }}><td style={{ padding: '7px 8px', fontWeight: 700 }}>NET SALES</td><td /><td /><td style={{ ...td, fontWeight: 700 }}>{money(gross - disc)}</td><td /><td /></tr>
            </>
          ) : null}
          renderLines={g => (
            <MiniTable head={[{ label: 'Date' }, { label: 'Invoice' }, { label: 'Customer' }, { label: 'Item / line' }, { label: 'Event' }, { label: 'Status' }, { label: 'Qty', right: true }, { label: 'Amount', right: true }]}
              rows={g.lines.map(l => (
                <tr key={l.id}>
                  <td style={sc}>{fmtDay(l.date)}</td>
                  <td style={sc}><Link to={`/invoices/${l.invoice_id}`}>{l.invoice_number}</Link></td>
                  <td style={sc}>{l.customer}</td>
                  <td style={sc}>{l.line_name}</td>
                  <td style={sc}>{l.event_name || ''}</td>
                  <td style={sc}>{l.status}</td>
                  <td style={scR}>{num(l.quantity)}</td>
                  <td style={scR}>{money(l.line_total)}</td>
                </tr>
              ))} />
          )}
        />
      )}
      <p className="form-hint no-print" style={{ marginTop: 10 }}>
        Every line of every invoice dated in the period (drafts and void invoices left out). Item type comes from what the item is linked to:
        an event sponsorship level, an event ticket, membership dues, or a stand-alone item. Click a row to see the invoice lines behind it.
      </p>
    </Shell>
  );
}

// ─── Expenses by vendor ───────────────────────────────────────────────────────
export function VendorExpenseReport() {
  const [range, setRange] = useState(rangePreset('ytd'));
  const [by, setBy] = useState('vendor');
  const [data, setData] = useState(null);
  useEffect(() => { loadFiscalStart().then(m => { if (m !== 1) setRange(rangePreset('fy_to_date')); }); }, []);
  useEffect(() => {
    setData(null);
    api.get(`/fin-reports/vendor-expenses?from=${range.from}&to=${range.to}`).then(r => setData(r.data))
      .catch(() => toast.error('Could not load expenses.'));
  }, [range]);
  const groups = useMemo(() => {
    if (!data) return [];
    const m = new Map();
    data.lines.forEach(l => {
      const k = by === 'vendor' ? (l.vendor_id ? `v${l.vendor_id}` : `p${l.payee.toLowerCase()}`) : `a${l.account_id}`;
      const x = m.get(k) || { key: k, label: by === 'vendor' ? l.payee : l.account_name, note: by === 'vendor' && !l.vendor_id ? 'not on the vendor list' : '', lines: [], amount: 0 };
      x.lines.push(l); x.amount += parseFloat(l.amount || 0); m.set(k, x);
    });
    return Array.from(m.values()).sort((a, b) => b.amount - a.amount);
  }, [data, by]);
  const total = groups.reduce((s, g) => s + g.amount, 0);
  return (
    <Shell controls={<>
      <RangePicker value={range} onChange={setRange} />
      <select className="form-select" style={{ width: 'auto', height: 34 }} value={by} onChange={e => setBy(e.target.value)}>
        <option value="vendor">By vendor</option>
        <option value="account">By expense account</option>
      </select>
    </>}>
      <div style={{ textAlign: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700, fontSize: '1.15rem' }}>Expenses by {by === 'vendor' ? 'Vendor' : 'Account'}</div>
        <div style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{fmtDay(range.from)} – {fmtDay(range.to)}</div>
      </div>
      {!data ? <div className="spinner" /> : (
        <GroupTable
          groups={groups}
          total={{ amount: total, n: data.lines.length }}
          cols={[
            { label: 'Transactions', get: g => g.lines.length, total: t => t.n },
            { label: 'Amount', get: g => <strong>{money(g.amount)}</strong>, total: t => money(t.amount) },
            { label: '% of Expenses', get: g => (total ? `${((g.amount / total) * 100).toFixed(1)}%` : ''), total: () => (total ? '100%' : '') },
          ]}
          renderLines={g => (
            <MiniTable head={[{ label: 'Date' }, { label: 'Type' }, { label: 'No.' }, { label: by === 'vendor' ? 'Account' : 'Vendor / name' }, { label: 'Paid from' }, { label: 'Memo' }, { label: 'Amount', right: true }]}
              rows={g.lines.map(l => (
                <tr key={l.line_id}>
                  <td style={sc}>{fmtDay(l.date)}</td>
                  <td style={sc}>{KIND[l.kind] || REF[l.reference_type] || 'Journal'}</td>
                  <td style={sc}>{l.number}</td>
                  <td style={sc}>{by === 'vendor' ? l.account_name : l.payee}</td>
                  <td style={sc}>{l.paid_from || ''}</td>
                  <td style={{ ...sc, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.memo}>{l.memo}</td>
                  <td style={{ ...scR, color: l.amount < 0 ? 'var(--success)' : undefined }}>{money(l.amount)}</td>
                </tr>
              ))} />
          )}
        />
      )}
      <p className="form-hint no-print" style={{ marginTop: 10 }}>
        Everything posted to expense accounts in the period. Names marked “not on the vendor list” were typed in by hand —
        add them under Bookkeeping → Vendors and pick the vendor on the transaction to group them properly. Refunds show as negative amounts.
      </p>
    </Shell>
  );
}
